import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FaceIcon from '@mui/icons-material/Face'
import ListIcon from '@mui/icons-material/List'
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ProvidersWorkUnitsInboxListResponse } from '@fireflyhealth/api'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Transition, TransitionGroup } from 'react-transition-group'
import type { TransitionStatus } from 'react-transition-group'
import keyBy from 'lodash/keyBy'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { asArrayOf, isFiniteNumber, isRecord, isStringValue, parseJson } from '~/shared/lib/guards'
import { useLocation } from 'react-router'
import { useQueryParam } from '~/shared/hooks/useQueryParam'
import { type CarePlanTemplateId, useInbox } from './InboxService'
import { TaskEditor, useAddTask, useUpdateTask, type TaskFormDraft } from '~/entities/task'
import {
  CheckBox,
  ErrorScreen,
  FFElation,
  InterfaceContent,
  PriorityIcon,
  SectionCard,
} from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { ErrorBoundary } from 'react-error-boundary'
import {
  CaseTag,
  NextActionDate,
  SlaCountdownPill,
  type CaseAction,
  type CaseTagAssociation,
} from '~/entities/work-unit'
import { Link as RouterLink } from 'react-router'
import { addDays, endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns'
import { getDueDateLabel } from '~/shared/lib/dateLabels'
import sortBy from 'lodash/sortBy'
import { useLocalStorage } from '~/shared/hooks/useLocalStorage'
import { useSelector } from 'react-redux'
import { useUpdateCase, workunitCompleteStates } from '~/entities/case'
import { formatRFC3339DateTimeOrEmpty, ISO_DATE_FORMAT, setDueDateToEOD } from '~/shared/lib/date'
import { usePodFilterOptions } from './usePodFilterOptions'
import TodoTypeSelect, { type SelectedTypeFilters } from './TodoTypeSelect'
import OwnerGroupFilter from './OwnerGroupFilter'
import { useGroupOptions, useMyAssigneeGroups, UserGroupSelect } from '~/entities/assignee-group'
import { InboxRowSelectedPayload, logEvent } from '~/shared/lib/events'
import { personRowName } from '~/entities/person'
import { haveChanged } from '~/shared/lib/haveChanged'
type RawInboxResult = ProvidersWorkUnitsInboxListResponse['results'][number]
type RawTaskItem = RawInboxResult['items'][number]

// The exit animation collapses each cell's contents too: a row's height comes from them.
const INBOX_TABLE_SX = {
  '& tr:hover [data-show-on-row-hover]': { opacity: 1, transition: '100ms linear opacity' },
  '& tr.inbox-row-exiting': {
    transform: 'translateX(100%)',
    transition: 'transform 200ms cubic-bezier(0.4, 0.0, 1, 1)',
    '& *': {
      '@keyframes shrink': { '0%': { maxHeight: '100vh' }, '100%': { maxHeight: 0 } },
      animation: 'shrink 200ms cubic-bezier(0.4, 0.0, 0.2, 1) 200ms',
    },
  },
}

const EXPAND_LINK_SX = { '&:hover': { cursor: 'pointer', color: 'primary.main' } }

const SPARKLE_SX = {
  '@keyframes sparkle': { '0%': { opacity: 0 }, '5%': { opacity: 1 }, '100%': { opacity: 0 } },
  animation: 'sparkle 6s linear 0s infinite normal',
  '&:nth-of-type(1)': { animationDelay: '1s' },
  '&:nth-of-type(2)': { animationDelay: '2s' },
  '&:nth-of-type(3)': { animationDelay: '3s' },
  '&:nth-of-type(4)': { animationDelay: '4s' },
  '&:nth-of-type(5)': { animationDelay: '5s' },
}

// Context for passing down filter state.
const Context = React.createContext<{
  filteredOwnerGroupIds?: number[]
}>({})

interface PodFilterOption {
  name: string
  id: number
}

const Inbox = () => {
  const me = useSelector(state => state.me)

  const [ownerGroupIds, setOwnerGroupIds] = useQueryParam<number[] | undefined>(
    'ownerGroupIds',
    me.assignee_group != undefined ? [me.assignee_group.id] : undefined,
    {
      parse: value => (value as string | undefined)?.split(',').map(param => Number(param)),
      stringify: value => value?.join(',') || null,
    },
  )
  const [podIds, setPodIds] = useQueryParam<number[] | undefined>('podIds', undefined, {
    parse: value => (value as string | undefined)?.split(',').map(param => Number(param)),
    stringify: value => value?.join(',') || null,
  })
  const [dueDateFilter, setDueDateFilter] = useQueryParam<NextActionDateFilterOption | undefined>(
    'nextActionDate',
    'Today',
  )
  const dueDateRange = dueDateFilter ? NEXT_ACTION_DATE_FILTER_TO_RANGE[dueDateFilter] : undefined
  const [typeFilters, setTypeFilters] = useQueryParam<SelectedTypeFilters | undefined>(
    'types',
    undefined,
    {
      // A filter's `type` and `id` become caseCategoryIds/carePlanAutocreatedFromIds on the request,
      // so an entry that does not match reads as no filter at all. Deliberately silent: useQueryParam
      // calls `parse` on every render, so a warning here would repeat rather than report once.
      parse: value =>
        asArrayOf(isStringValue(value) ? parseJson(value) : null, isTypeFilter) ?? undefined,
      stringify: value => JSON.stringify(value),
    },
  )
  const [page, setPage] = useState(0)
  const [pageSizePref, setPageSizePref] = useLocalStorage('inbox.pageSize')
  const pageSize = pageSizePref ? parseInt(pageSizePref) : 25

  const { data: myGroups } = useMyAssigneeGroups()

  // Opting in is additive on purpose: the default stays the user's own queue, and adding one set of
  // groups never drops another. Clicking an already-added chip takes just its own groups back out.
  const selectedGroupIds = new Set(ownerGroupIds ?? [])
  const groupsAreInView = (groups: { id: number }[]) =>
    groups.length > 0 && groups.every(group => selectedGroupIds.has(group.id))
  const toggleGroups = (groups: { id: number }[]) => {
    const ids = groups.map(group => group.id)
    setOwnerGroupIds(
      groupsAreInView(groups)
        ? (ownerGroupIds ?? []).filter(id => !ids.includes(id))
        : [...new Set([...(ownerGroupIds ?? []), ...ids])],
    )
  }

  // A chip has to say whether its groups are in the view — MUI's click styling is transient, so
  // without this the only feedback fades as soon as focus moves elsewhere.
  const coverageChip = (testId: string, name: string, groups: { id: number }[]) => {
    const inView = groupsAreInView(groups)
    return (
      <Chip
        label={`${inView ? '' : '+ '}${name} (${groups.length})`}
        size="small"
        color={inView ? 'primary' : 'default'}
        variant={inView ? 'filled' : 'outlined'}
        icon={inView ? <CheckIcon fontSize="small" /> : undefined}
        onClick={() => toggleGroups(groups)}
        aria-pressed={inView}
        data-testid={testId}
      />
    )
  }

  // "Nice work!" belongs to any view made only of the user's own groups, not just their personal
  // one — a coverage partner who cleared a covered queue has earned it too.
  const assigneeGroupId = me.assignee_group?.id
  const myGroupIds = useMemo(() => {
    const ids = assigneeGroupId != null ? [assigneeGroupId] : []
    if (myGroups) {
      if (myGroups.personal) ids.push(myGroups.personal.id)
      myGroups.covering.forEach(group => ids.push(group.id))
      myGroups.teams.forEach(group => ids.push(group.id))
    }
    return new Set(ids)
  }, [assigneeGroupId, myGroups])
  const viewingOnlyMyGroups =
    !!ownerGroupIds?.length && ownerGroupIds.every(id => myGroupIds.has(id))

  const { data, isLoading } = useInbox({
    ownerGroupIds: ownerGroupIds ?? undefined,
    podIds,
    dueDateRange: dueDateRange,
    carePlanAutocreatedFromIds: typeFilters
      ? typeFilters.filter(filter => filter.type === 'Care Plan').map(template => template.id)
      : undefined,
    caseCategoryIds: typeFilters
      ? typeFilters
          .filter(filter => filter.type === 'Case Category')
          .map(category => category.id)
          // Only care plans carry the `None` sentinel; a category is always a numeric id.
          .filter(isFiniteNumber)
      : undefined,
    limit: pageSize,
    offset: page * pageSize,
  })
  const podFilterOptions = usePodFilterOptions()
  const isNoCareTeam = podFilterOptions?.some(podFilterOption => {
    return podFilterOption.id === 0
  })
  if (!isNoCareTeam || !podFilterOptions || podFilterOptions.length == 0) {
    podFilterOptions.push({
      id: 0,
      name: 'No Care Team',
    })
  }
  // A change to any filter goes back to the first page.
  const filters = [dueDateRange, typeFilters, ownerGroupIds, podIds]
  const [pagedFilters, setPagedFilters] = useState(filters)
  if (haveChanged(pagedFilters, filters)) {
    setPagedFilters(filters)
    setPage(0)
  }

  // Whenever the search parameters change, update the key in local storage so
  // that they remain "sticky".
  const location = useLocation()
  const [__, setInboxParams] = useLocalStorage('inboxParams')
  useEffect(() => {
    setInboxParams(location.search)
  }, [setInboxParams, location.search])

  // The triage table is one JSX block rendered bare (legacy) or inside a
  // SectionCard with gutters (design refresh). All data and handlers shared.
  const triageTable = (
    <TableContainer>
      <Table sx={INBOX_TABLE_SX}>
        <TableHead>
          <TableRow>
            <TablePagination
              count={data?.count ?? 0}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_event, newPage) => {
                setPage(newPage)
              }}
              onRowsPerPageChange={event => {
                setPageSizePref(event.target.value)
              }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          <Context.Provider
            value={{
              filteredOwnerGroupIds: ownerGroupIds,
            }}
          >
            <TransitionGroup component={null}>
              {data?.results?.map((result, i) => (
                // We want to animate rows as they exit to make the
                // transition less jarring. Material UI provides
                // transition components, but these can only take a single
                // child, and each InboxRow is potentially two TableRows
                // (the second for the collapsible content). DOM
                // validation prohibits anything but a TableRow from being
                // a child of a TableBody.
                //
                // To get around these constraints, we fall back to using
                // the bare Transition component, and pass down props to
                // each TableRow.
                <InboxRowTransition
                  key={`${result.content_object.content_type}-${result.object_id}`}
                  timeout={400}
                >
                  {(status, nodeRef) => (
                    <InboxRow
                      TableRowProps={{
                        ref: nodeRef,
                        className:
                          status === 'exiting' || status === 'exited'
                            ? 'inbox-row-exiting'
                            : undefined,
                        sx: {
                          borderTop: 'solid',
                          borderWidth: '.15rem',
                          borderColor: 'grey.100',
                        },
                      }}
                      workUnit={result}
                      dueDateRange={dueDateRange}
                      positionalInfo={{
                        pagePosition: i,
                        absolutePosition: page * pageSize + i,
                      }}
                    />
                  )}
                </InboxRowTransition>
              ))}
            </TransitionGroup>
          </Context.Provider>
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <InterfaceContent>
      {isLoading && <LinearProgress />}
      <ErrorBoundary FallbackComponent={ErrorScreen}>
        <Box sx={{ display: 'flex', padding: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid size={3}>
                <OwnerGroupFilter
                  value={ownerGroupIds}
                  onChange={value => setOwnerGroupIds(value)}
                  label="Assigned to"
                  myGroups={myGroups}
                />
              </Grid>
              <Grid size={3}>
                <PodFilter
                  value={podIds}
                  onChange={value => setPodIds(value)}
                  podFilterOptions={podFilterOptions}
                />
              </Grid>
              <Grid size={3}>
                <NextActionDateFilter
                  value={dueDateFilter}
                  onChange={value => setDueDateFilter(value)}
                />
              </Grid>
              <Grid size={3}>
                <TodoTypeSelect
                  data={data}
                  currentSelections={typeFilters}
                  onClose={selected =>
                    setTypeFilters(
                      (selected || []).map(option => ({
                        type: option.type,
                        id: option.id,
                        label: option.label,
                      })) ?? [],
                    )
                  }
                />
              </Grid>
            </Grid>
            {myGroups?.covering.length || myGroups?.teams.length ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Add to this view:
                </Typography>
                {/* Two chips, not one: covering for someone and leading a team are separate
                    decisions, so each is its own deliberate click. Each chip shows whether its
                    groups are currently in the view and toggles them. */}
                {myGroups.covering.length
                  ? coverageChip('add-covering-groups', 'Covering', myGroups.covering)
                  : null}
                {myGroups.teams.length
                  ? coverageChip('add-team-groups', 'My teams', myGroups.teams)
                  : null}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            {!isLoading && viewingOnlyMyGroups && data?.results?.length === 0 ? (
              <Grid
                container
                sx={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}
              >
                <Grid>
                  <Sparkle>
                    <Typography sx={{ fontWeight: 'bold' }}>Nice work!</Typography>
                  </Sparkle>
                </Grid>
              </Grid>
            ) : (
              // The triage table sits in a carded frame with gutters.
              <Box sx={{ px: 2, pb: 2 }}>
                <SectionCard disablePadding>{triageTable}</SectionCard>
              </Box>
            )}
          </Box>
        </Box>
      </ErrorBoundary>
    </InterfaceContent>
  )
}

// Helper for using query params as state.
const isCarePlanTemplateId = (value: unknown): value is CarePlanTemplateId =>
  isFiniteNumber(value) || value === 'None'

const isTypeFilter = (value: unknown): value is SelectedTypeFilters[number] =>
  isRecord(value) &&
  (value.type === 'Care Plan' || value.type === 'Case Category') &&
  isCarePlanTemplateId(value.id) &&
  isStringValue(value.label)

const PodFilter = ({
  value,
  onChange,
  podFilterOptions,
}: {
  value?: number[]
  onChange: (value?: number[]) => void
  podFilterOptions?: PodFilterOption[]
}) => {
  const podsByID = useMemo(() => keyBy(podFilterOptions, 'id'), [podFilterOptions])
  return (
    <Autocomplete
      size="small"
      multiple
      options={podFilterOptions ?? []}
      getOptionLabel={pod => `${pod.name}`}
      isOptionEqualToValue={(option, value) => option.id == value.id}
      filterSelectedOptions
      renderInput={params => <TextField {...params} label={'Care pods'} />}
      renderOption={({ key, ...props }, pod) => (
        <li key={key} {...props}>
          <span>{pod.name}</span>
        </li>
      )}
      value={value?.map(id => podsByID[id] ?? { id, name: `Pod ${id}` }) ?? []}
      onChange={(_, selected) => {
        onChange(selected?.map(pod => pod.id) ?? [])
      }}
      disableCloseOnSelect
    />
  )
}

const NEXT_ACTION_DATE_FILTER_TO_RANGE = {
  Today: {
    end: endOfDay(new Date()),
  },
  'Next 7 Days': {
    start: startOfDay(new Date()),
    end: endOfDay(addDays(new Date(), 7)),
  },
  Overdue: {
    end: endOfDay(subDays(new Date(), 1)),
  },
}

type NextActionDateFilterOption = keyof typeof NEXT_ACTION_DATE_FILTER_TO_RANGE

const NextActionDateFilter = ({
  value,
  onChange,
}: {
  value?: NextActionDateFilterOption
  onChange: (value?: NextActionDateFilterOption) => void
}) => {
  return (
    <Autocomplete<NextActionDateFilterOption>
      size="small"
      options={Object.keys(NEXT_ACTION_DATE_FILTER_TO_RANGE) as NextActionDateFilterOption[]}
      value={value}
      renderInput={params => <TextField {...params} label="Next action date" />}
      onChange={(_, selected) => {
        onChange(selected ?? undefined)
      }}
    />
  )
}

// TransitionGroup drives each child by cloning it with `in`/`onExited`, so this
// forwards every Transition prop and adds only the nodeRef — without one,
// react-transition-group falls back to ReactDOM.findDOMNode, which React 19
// removes. The ref lands on the row's first TableRow via TableRowProps.
const InboxRowTransition = ({
  children,
  ...transitionProps
}: {
  // What the call site sets, plus what TransitionGroup injects when cloning.
  timeout: number
  in?: boolean
  appear?: boolean
  enter?: boolean
  exit?: boolean
  onExited?: () => void
  children: (
    status: TransitionStatus,
    nodeRef: React.RefObject<HTMLTableRowElement | null>,
  ) => React.ReactNode
}) => {
  const nodeRef = useRef<HTMLTableRowElement>(null)
  return (
    <Transition {...transitionProps} nodeRef={nodeRef}>
      {status => children(status, nodeRef)}
    </Transition>
  )
}

const InboxRow = ({
  TableRowProps,
  workUnit,
  dueDateRange,
  positionalInfo,
}: {
  TableRowProps?: React.ComponentPropsWithRef<typeof TableRow>
  workUnit: RawInboxResult
  dueDateRange?: {
    start?: Date
    end: Date
  }
  positionalInfo: {
    pagePosition: number
    absolutePosition: number
  }
}) => {
  const logInboxItemSelection = (urlType: InboxRowSelectedPayload['urlType']) => {
    // Analytics tracking to understand how users are intereacting with items in the Inbox
    // e.g. how far down on the page are they scrolling to pick up work?
    void logEvent<InboxRowSelectedPayload>('INBOX_ROW_SELECTED', {
      ...positionalInfo,
      urlType,
      contentType: workUnit.content_object.content_type || '',
      contentObjectId: workUnit.object_id,
    })
  }

  switch (workUnit.content_object.content_type) {
    case 'careplan': {
      return (
        <CarePlanInboxRow
          TableRowProps={TableRowProps}
          carePlan={workUnit}
          dueDateRange={dueDateRange}
          onClickCapture={logInboxItemSelection}
        />
      )
    }
    case 'case': {
      return (
        <CaseInboxRow
          TableRowProps={TableRowProps}
          case={workUnit}
          dueDateRange={dueDateRange}
          onClickCapture={logInboxItemSelection}
        />
      )
    }
    case 'insurancecardinfo': {
      return (
        <InsuranceCardInfoInboxRow
          TableRowProps={TableRowProps}
          insuranceCardInfo={workUnit}
          dueDateRange={dueDateRange}
          onClickCapture={logInboxItemSelection}
        />
      )
    }
  }

  // Unknown content type!
  return (
    <InboxRowContent
      TableRowProps={TableRowProps}
      person={{
        first_name: 'Unknown',
        last_name: 'Unknown',
      }}
      title="Unknown type"
      subtitle={`Please report in #firefly-support that ${workUnit.content_object.content_type} ID ${workUnit.object_id} is not appearing properly in the inbox`}
      dueDate={workUnit.due_date}
      dueDateRange={dueDateRange}
      canChangeOwner={false}
      canAddTask={false}
    />
  )
}

const CarePlanInboxRow = ({
  TableRowProps,
  carePlan,
  dueDateRange,
  onClickCapture,
}: {
  TableRowProps?: React.ComponentPropsWithRef<typeof TableRow>
  carePlan: RawInboxResult
  dueDateRange?: {
    start?: Date
    end: Date
  }
  onClickCapture: InboxRowContentProps['onClickCapture']
}) => {
  const careplan = carePlan.content_object as Extract<
    RawInboxResult['content_object'],
    { content_type: 'careplan' }
  >
  return (
    <InboxRowContent
      TableRowProps={TableRowProps}
      person={careplan.patient}
      url={`/patients/${careplan.patient.id}/careplans`}
      urlType="profile_care_plans"
      title={careplan.title}
      dueDate={carePlan.due_date}
      tasks={carePlan.items}
      dueDateRange={dueDateRange}
      canChangeOwner={false}
      canAddTask={false}
      onClickCapture={onClickCapture}
    />
  )
}

const CaseInboxRow = (props: {
  TableRowProps?: React.ComponentPropsWithRef<typeof TableRow>
  case: RawInboxResult
  dueDateRange?: {
    start?: Date
    end: Date
  }
  onClickCapture: InboxRowContentProps['onClickCapture']
}) => {
  const { mutateAsync: handleUpdateCase, isPending: isLoading } = useUpdateCase()

  const { mutateAsync: handleUpdateCaseStatus, isPending: isLoadingStatus } = useUpdateCase()

  const caseObject = props.case.content_object as Extract<
    RawInboxResult['content_object'],
    { content_type: 'case' }
  >

  return (
    <InboxRowContent
      TableRowProps={props.TableRowProps}
      person={caseObject.person}
      url={`/people/${caseObject.person.id}/todos?caseId=${props.case.object_id}`}
      urlType="case"
      ownerGroup={caseObject.owner_group ?? null}
      title={caseObject.category.title}
      subtitle={caseObject.description ?? ''}
      status={caseObject.status ?? ''}
      actions={caseObject.actions}
      statusCategories={caseObject.states_with_categories}
      onAction={action =>
        fireAndForget('[Inbox] update case status', () =>
          handleUpdateCaseStatus({
            id: props.case.object_id,
            body: { action },
          }),
        )
      }
      dueDate={caseObject.due_date}
      onChangeDueDate={dueDate =>
        fireAndForget('[Inbox] update case due date', () =>
          handleUpdateCase({
            id: props.case.object_id,
            body: { due_date: dueDate ? setDueDateToEOD(dueDate) : undefined },
          }),
        )
      }
      onChangeOwnerGroup={ownerGroup =>
        fireAndForget('[Inbox] update case owner group', () =>
          handleUpdateCase({
            id: props.case.object_id,
            body: { owner_group: ownerGroup },
          }),
        )
      }
      tasks={props.case.items}
      messageCount={caseObject?.message_count ?? 0}
      earliestCompletionSlaAt={props.case.earliest_completion_sla_at}
      earliestCompletionSlaTaskType={props.case.earliest_completion_sla_task_type}
      loading={isLoading}
      loadingStatus={isLoadingStatus}
      dueDateRange={props.dueDateRange}
      canChangeOwner={true}
      canAddTask={true}
      taskRelation={{
        content_type: 'case',
        object_id: props.case.object_id,
      }}
      tags={caseObject.case_tags ?? undefined}
      onClickCapture={props.onClickCapture}
    />
  )
}

const InsuranceCardInfoInboxRow = ({
  TableRowProps,
  insuranceCardInfo,
  dueDateRange,
  onClickCapture,
}: {
  TableRowProps?: React.ComponentPropsWithRef<typeof TableRow>
  insuranceCardInfo: RawInboxResult
  dueDateRange?: {
    start?: Date
    end: Date
  }
  onClickCapture: InboxRowContentProps['onClickCapture']
}) => {
  const co = insuranceCardInfo.content_object as Extract<
    RawInboxResult['content_object'],
    { content_type: 'insurancecardinfo' }
  >
  return (
    <InboxRowContent
      TableRowProps={TableRowProps}
      person={co.person}
      url={`/people/${co.person.id}/profile`}
      urlType="profile"
      title="Insurance Upload"
      dueDate={insuranceCardInfo.due_date}
      tasks={insuranceCardInfo.items}
      dueDateRange={dueDateRange}
      canChangeOwner={false}
      canAddTask={false}
      onClickCapture={onClickCapture}
    />
  )
}

const InboxTaskRow = ({ task, careTeam }: { task: RawTaskItem; careTeam?: number[] }) => {
  const [isChecked, setIsChecked] = useState(task.content_object.is_complete)

  const { isPending: isLoading, mutate: updateTask } = useUpdateTask()

  const onChangeDueDate = (dueDate: string) => {
    const formattedDueDate = dueDate ? formatRFC3339DateTimeOrEmpty(dueDate) : undefined
    updateTask({ id: task.content_object.id, due_date: formattedDueDate })
  }

  const { selectedCaseOwnerGroupOption = null, flatOptions } = useGroupOptions(
    task.content_object.owner_group?.id || null,
    false,
    null,
    careTeam,
    false,
  )

  return (
    <TableRow>
      <TableCell></TableCell>
      <TableCell>
        <Grid container sx={{ flexWrap: 'nowrap' }}>
          <Grid>
            <CheckBox
              disabled={isLoading}
              value={isChecked}
              onChecked={value => {
                setIsChecked(value)
                updateTask(
                  { id: task.content_object.id, is_complete: value },
                  { onError: () => setIsChecked(!value) },
                )
              }}
            ></CheckBox>
          </Grid>
          <Grid container size={12} sx={{ flexDirection: 'column', justifyContent: 'flex-start' }}>
            <Grid>
              <Box sx={{ display: 'inline-flex' }}>
                <Box sx={{ marginRight: '0.5rem' }}>
                  {task.content_object.priority ? (
                    <PriorityIcon level={task.content_object.priority} variant="view" />
                  ) : null}
                </Box>
                <Typography style={{ overflowWrap: 'anywhere' }} sx={{ display: 'inline' }}>
                  {task.content_object.title}
                </Typography>
              </Box>
            </Grid>
            <Grid>
              <Typography sx={{ color: 'secondary.main' }}>
                Created {getDueDateLabel(parseISO(task.content_object.created_at))}
                {task.content_object.created_by
                  ? ` by ${task.content_object.created_by.first_name} ${task.content_object.created_by.last_name}`
                  : null}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
        <Grid container sx={{ justifyContent: 'flex-start', flexDirection: 'column' }}>
          <Grid>
            <UserGroupSelect
              selectedValue={selectedCaseOwnerGroupOption ?? null}
              options={flatOptions}
              onChange={ownerGroupOption =>
                updateTask({ id: task.content_object.id, owner_group: ownerGroupOption.value })
              }
              detailed
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </TableCell>
      <TableCell></TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Grid container>
          <Grid>
            <NextActionDate
              date={task.content_object.due_date}
              editable={true}
              onChange={onChangeDueDate}
            />
          </Grid>
        </Grid>
      </TableCell>
      <TableCell></TableCell>
    </TableRow>
  )
}

const ConditionalLink: React.FC<
  React.PropsWithChildren<{
    to?: string
    urlType?: InboxRowContentProps['urlType']
    onClickCapture: InboxRowContentProps['onClickCapture']
  }>
> = ({ to, children, urlType, onClickCapture }) => {
  if (!to) {
    return <>{children}</>
  }

  return (
    <Link
      component={RouterLink}
      sx={{
        textDecoration: 'none',
        cursor: 'pointer',
        transition: '100ms linear color',
        color: 'inherit',
        '&:hover': {
          color: 'primary.main',
        },
      }}
      to={to}
      onClickCapture={_event => (onClickCapture ? onClickCapture(urlType) : undefined)}
    >
      {children}
    </Link>
  )
}

interface InboxRowContentProps {
  TableRowProps?: React.ComponentPropsWithRef<typeof TableRow>
  person: {
    id?: number
    first_name: string | null
    last_name: string | null
    preferred_name?: string | null
    elation_url?: string | null
    careTeam?: number[]
    user_id?: number | null
  }
  url?: string
  urlType?: InboxRowSelectedPayload['urlType']
  owner?: {
    id: number
  } | null
  ownerGroup?: {
    id: number
  } | null
  title: string
  subtitle?: string
  status?: string
  actions?: CaseAction[]
  statusCategories?: { state: { name: string }; category: string }[]

  onAction?: (action: string | undefined) => void
  dueDate?: string | null
  onChangeDueDate?: (newDate: string) => void
  onChangeOwnerGroup?: (newOwnerGroup: number) => void
  tasks?: RawTaskItem[]
  messageCount?: number
  earliestCompletionSlaAt?: string | null
  earliestCompletionSlaTaskType?: 'urgent' | 'normal' | null
  loading?: boolean
  loadingStatus?: boolean
  dueDateRange?: {
    start?: Date
    end: Date
  }
  canChangeOwner: boolean
  canAddTask: boolean
  taskRelation?: {
    content_type: string
    object_id: number
  }
  tags?: CaseTagAssociation[]
  onClickCapture?: (urlType: InboxRowContentProps['urlType']) => void
}

const InboxRowContent = ({
  TableRowProps,
  person,
  url,
  urlType,
  ownerGroup,
  title,
  subtitle,
  status,
  actions,
  statusCategories,
  onAction,
  dueDate,
  onChangeDueDate,
  onChangeOwnerGroup,
  tasks,
  messageCount,
  earliestCompletionSlaAt,
  earliestCompletionSlaTaskType,
  loading,
  loadingStatus,
  dueDateRange,
  canChangeOwner,
  canAddTask,
  taskRelation,
  tags,
  onClickCapture,
}: InboxRowContentProps) => {
  const { filteredOwnerGroupIds } = useContext(Context)

  const { isPending: isLoading, mutate: handleCreateTask } = useAddTask()

  const me = useSelector(state => state.me)

  const newTaskData = {
    title: '',
    due_date: format(new Date(), ISO_DATE_FORMAT),
    ...{ owner_group: me.assignee_group?.id },
    priority: 0,
    is_complete: false,
  }

  const [addingTask, setAddingTask] = useState<boolean>(false)
  const [newTask, setNewTask] = useState<TaskFormDraft>(newTaskData)

  const [isOpen, setIsOpen] = useState(Boolean(tasks?.length))
  const [showAllIncomplete, setShowAllIncomplete] = useState(false)

  const incompleteTasks = tasks?.filter(task => task.content_object.is_complete == false)
  const sortedIncompleteTasks = sortBy(incompleteTasks, [
    function (task) {
      return task.content_object.due_date
    },
  ])

  const availableActions =
    // If there are incomplete tasks, don't allow moving the parent to a completed state
    incompleteTasks && statusCategories && incompleteTasks.length > 0
      ? actions?.filter(
          action =>
            // Widen the known array rather than casting the API string to StatusCategory,
            // since .category from the raw API response is typed as string, not StatusCategory.
            !(workunitCompleteStates as Array<string | undefined>).includes(
              statusCategories?.find(
                stateWithCategory => stateWithCategory['state']['name'] == action.dest,
              )?.category,
            ),
        )
      : actions

  const handleAddTask = () => {
    const payload = {
      title: newTask.title,
      owner_group: newTask.owner_group,
      due_date: formatRFC3339DateTimeOrEmpty(newTask.due_date),
      priority: newTask.priority,
      person: person.id,
      patient: person.user_id ?? undefined,
      relations: taskRelation ? [taskRelation] : [],
      is_complete: false,
    }
    handleCreateTask(payload)
    setAddingTask(!addingTask)
    setNewTask(newTaskData)
  }

  const isTaskHidden = (task: RawTaskItem) => {
    // If we are not filtering by any owner, show all incomplete tasks.
    if (!filteredOwnerGroupIds?.length) {
      return false
    }
    // Only show tasks due in the due date filter range
    const taskDueDate = new Date(task.content_object.due_date ?? '')
    // Some due date filter range does not have a start date
    const isTaskDueDateAfterStartFilter =
      !dueDateRange?.start || (dueDateRange?.start && taskDueDate >= dueDateRange.start)
    const isTaskInDateRange =
      (isTaskDueDateAfterStartFilter && dueDateRange?.end && taskDueDate <= dueDateRange.end) ||
      !dueDateRange
    if (!isTaskInDateRange) return true

    // User/group filters
    if (
      task.content_object.owner_group?.id &&
      filteredOwnerGroupIds?.includes(task.content_object.owner_group.id)
    ) {
      return false
    }

    return true
  }

  const numHiddenTasks = incompleteTasks?.filter(task => isTaskHidden(task)).length

  const { selectedCaseOwnerGroupOption, flatOptions } = useGroupOptions(
    ownerGroup?.id ?? null,
    false,
    null,
    // The next line is a lie - cause the person object from ProvidersWorkUnitsInboxListResponse does not contain a care team
    // Meaning - the care team will never show up in task assignment in the todos page
    person?.careTeam,
    !canChangeOwner,
  )

  return (
    <>
      <TableRow {...TableRowProps}>
        <TableCell sx={{ verticalAlign: 'top', minWidth: '20vw' }}>
          <Grid
            container
            sx={{ flexDirection: 'column', justifyContent: 'flex-start', marginBottom: -1 }}
          >
            <Grid>
              <ConditionalLink
                to={url}
                onClickCapture={_event => (onClickCapture ? onClickCapture(urlType) : undefined)}
              >
                <Typography sx={{ fontWeight: 'bold' }}>{personRowName(person)}</Typography>
              </ConditionalLink>
            </Grid>
            <Grid
              container
              size={12}
              data-show-on-row-hover
              sx={{ opacity: 0, transition: '100ms linear opacity' }}
            >
              {url ? (
                <Grid>
                  <Link
                    component={RouterLink}
                    to={url}
                    onClickCapture={_event =>
                      onClickCapture ? onClickCapture(urlType) : undefined
                    }
                    sx={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: '100ms linear color',
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    <FaceIcon fontSize="inherit" titleAccess="Go to patient" />
                    <Typography title="Go to patient" variant="caption" sx={{ display: 'inline' }}>
                      Go to patient
                    </Typography>
                  </Link>
                </Grid>
              ) : null}
              {person?.elation_url ? (
                <Grid sx={{ ml: 2 }}>
                  <Link
                    href={person.elation_url}
                    onClickCapture={_event =>
                      onClickCapture ? onClickCapture('elation') : undefined
                    }
                    target="blank"
                    sx={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: '100ms linear color',
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    <FFElation titleAccess="Open in Elation" />
                    <Typography
                      title="Open in Elation"
                      variant="caption"
                      sx={{ display: 'inline' }}
                    >
                      Open in Elation
                    </Typography>
                  </Link>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top', width: '100%' }}>
          <Grid container sx={{ flexDirection: 'column', justifyContent: 'flex-start' }}>
            <Grid>
              <ConditionalLink to={url} urlType={urlType} onClickCapture={onClickCapture}>
                <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
              </ConditionalLink>
            </Grid>
            <Grid>
              <Typography sx={{ color: 'secondary.main' }}>{subtitle}</Typography>
              {tags &&
                tags.length > 0 &&
                tags?.map(caseTag => {
                  return (
                    <CaseTag
                      key={String(caseTag.id)}
                      tagKey={String(caseTag.id)}
                      tag={caseTag.tag}
                    />
                  )
                })}
              <Grid>
                <SlaCountdownPill
                  earliestCompletionSlaAt={earliestCompletionSlaAt}
                  earliestCompletionSlaTaskType={earliestCompletionSlaTaskType}
                  messageCount={messageCount ?? 0}
                />
              </Grid>
            </Grid>
          </Grid>
        </TableCell>
        <TableCell align="right" sx={{ verticalAlign: 'top', minWidth: '20vw' }}>
          <Grid container sx={{ justifyContent: 'flex-start', flexDirection: 'column' }}>
            <Grid>
              <UserGroupSelect
                selectedValue={selectedCaseOwnerGroupOption ?? null}
                options={flatOptions}
                onChange={ownerGroupOption => {
                  const ownerGroup = ownerGroupOption.value
                  if (ownerGroup) onChangeOwnerGroup?.(ownerGroup)
                }}
                disabled={!canChangeOwner || loading}
                detailed
              />
            </Grid>
          </Grid>
        </TableCell>
        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
          <Grid container sx={{ justifyContent: 'flex-end' }}>
            <Grid>
              {availableActions ? (
                <Autocomplete
                  size="small"
                  disableClearable={true}
                  style={{ width: 180 }}
                  autoHighlight
                  value={{
                    dest: status ?? '',
                  }}
                  options={availableActions || []}
                  getOptionLabel={option => {
                    // `option` could be the option to represent the current value
                    // in which case we only know the current status
                    // or it could be a state transition
                    // in which case the trigger name is what we want to show
                    return option.trigger || (status ?? '')
                  }}
                  isOptionEqualToValue={(option, _value) => !!option.trigger}
                  onChange={(_event, newValue) => {
                    onAction?.(newValue.trigger)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{
                        '& fieldset': { border: 'none' },
                        '&': {
                          backgroundColor: 'tones.neutral.bg',
                          borderRadius: '10px',
                        },
                      }}
                    />
                  )}
                  disabled={loadingStatus}
                />
              ) : (
                (status ?? null)
              )}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Grid container>
            <Grid>
              <NextActionDate
                date={dueDate}
                editable={Boolean(onChangeDueDate)}
                onChange={onChangeDueDate}
              />
            </Grid>
          </Grid>
        </TableCell>
        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
          <Grid container sx={{ justifyContent: 'flex-end' }}>
            <Grid>
              <IconButton
                onClick={() => {
                  setIsOpen(!isOpen)
                }}
              >
                {isOpen ? (
                  <ArrowDropDownIcon color={incompleteTasks?.length ? 'primary' : undefined} />
                ) : (
                  <ArrowRightIcon color={incompleteTasks?.length ? 'primary' : undefined} />
                )}
                <Badge badgeContent={incompleteTasks?.length ?? null} color="primary">
                  <ListIcon color={incompleteTasks?.length ? 'primary' : undefined} />
                </Badge>
              </IconButton>
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      {isOpen
        ? sortedIncompleteTasks
            ?.filter(task => showAllIncomplete || !isTaskHidden(task))
            .map(task => (
              <InboxTaskRow key={task.object_id} task={task} careTeam={person?.careTeam} />
            ))
        : null}
      {isOpen && addingTask ? (
        <TableRow className={TableRowProps?.className}>
          <TableCell sx={{ verticalAlign: 'top', minWidth: '20vw' }} />
          <TableCell sx={{ verticalAlign: 'top', width: '100%' }} colSpan={2}>
            <Box sx={{ width: '93%', margin: '1rem 0 2rem 2rem' }}>
              <Paper>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ marginTop: 1, marginBottom: 4 }}>
                    <TaskEditor
                      disabled={isLoading}
                      task={newTask}
                      // The row's person is the only field the editor reads off
                      // the patient.
                      patient={{ person } as unknown as PatientUser}
                      onChange={task => {
                        setNewTask(task)
                      }}
                      canAssignToPatient={false}
                    />
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
                    <Box sx={{ ml: 1 }}>
                      <Button disabled={isLoading} onClick={() => setAddingTask(!addingTask)}>
                        Cancel
                      </Button>
                      <Button
                        disabled={isLoading || !newTask.title}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          handleAddTask()
                        }}
                      >
                        <Typography variant="button">Add Task</Typography>
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </TableCell>
        </TableRow>
      ) : null}
      {isOpen && !addingTask ? (
        <TableRow className={TableRowProps?.className}>
          <TableCell sx={{ verticalAlign: 'top', minWidth: '20vw' }} />
          <TableCell sx={{ verticalAlign: 'top', width: '100%' }}>
            <Grid
              container
              spacing="10"
              sx={{ flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'flex-end' }}
            >
              {canAddTask ? (
                <Grid>
                  <Link
                    onClick={() => {
                      setAddingTask(true)
                    }}
                    color="secondary"
                    underline="none"
                    sx={EXPAND_LINK_SX}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      <AddIcon fontSize="inherit" />
                      Add Task
                    </Typography>
                  </Link>
                </Grid>
              ) : null}
              {numHiddenTasks ? (
                <Grid>
                  <Link
                    onClick={() => {
                      setShowAllIncomplete(!showAllIncomplete)
                    }}
                    color="secondary"
                    underline="none"
                    sx={EXPAND_LINK_SX}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      <ExpandMoreIcon fontSize="inherit" />
                      {showAllIncomplete ? 'Hide' : 'Show'} all incomplete ({numHiddenTasks})
                    </Typography>
                  </Link>
                </Grid>
              ) : null}
            </Grid>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

const SPARKLE_RADIUS = 100

// A uniformly random point in the circle; see
// https://stackoverflow.com/questions/5837572/generate-a-random-point-within-a-circle-uniformly
const randomSparkleOffset = () => {
  const distance = SPARKLE_RADIUS * Math.sqrt(Math.random())
  const angle = Math.random() * 2 * Math.PI
  return `translate(${distance * Math.cos(angle)}px, ${distance * Math.sin(angle)}px)`
}

// Very important component...should evoke the feeling of having a
// sparkling-clean inbox.
const Sparkle: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [offsets] = useState(() => Array.from({ length: 5 }, randomSparkleOffset))

  return (
    <Box style={{ position: 'relative' }}>
      {offsets.map((transform, i) => (
        <Box key={i} sx={SPARKLE_SX} style={{ position: 'absolute', transform }}>
          ✨
        </Box>
      ))}
      <>{children}</>
    </Box>
  )
}

export default Inbox
